import { useState, useEffect } from 'react';
import { Mail, MailOpen, Trash2, X, Clock, Building2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const auth = localStorage.getItem('adminAuth');
  const config = { headers: { Authorization: `Basic ${auth}` } };

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/admin/messages`, config);
      setMessages(res.data);
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.put(`${API}/admin/messages/${id}/read`, {}, config);
      setMessages(messages.map(m => m.id === id ? {...m, read: true} : m));
      if (selectedMessage?.id === id) {
        setSelectedMessage({...selectedMessage, read: true});
      }
    } catch (err) {
      console.error('Error marking read:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce message?')) return;
    try {
      await axios.delete(`${API}/admin/messages/${id}`, config);
      setMessages(messages.filter(m => m.id !== id));
      if (selectedMessage?.id === id) {
        setSelectedMessage(null);
      }
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  const openMessage = (message) => {
    setSelectedMessage(message);
    if (!message.read) {
      handleMarkRead(message.id);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = messages.filter(m => !m.read).length;

  if (loading) {
    return <div className="text-text-secondary">Chargement...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-playfair text-3xl text-text-primary">Messages</h1>
          <p className="text-text-secondary mt-2">
            {messages.length} message{messages.length !== 1 ? 's' : ''} 
            {unreadCount > 0 && <span className="text-brand-gold ml-2">({unreadCount} non lu{unreadCount !== 1 ? 's' : ''})</span>}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Messages List */}
        <div className="lg:col-span-1 bg-surface-secondary border border-white/5 overflow-hidden">
          <div className="divide-y divide-white/5 max-h-[600px] overflow-y-auto">
            {messages.length === 0 ? (
              <div className="p-8 text-center text-text-muted">
                Aucun message pour le moment
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  onClick={() => openMessage(message)}
                  className={`p-4 cursor-pointer transition-colors ${
                    selectedMessage?.id === message.id 
                      ? 'bg-brand-gold/10 border-l-2 border-brand-gold' 
                      : 'hover:bg-white/5'
                  } ${!message.read ? 'bg-surface-tertiary' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 ${message.read ? 'text-text-muted' : 'text-brand-gold'}`}>
                      {message.read ? <MailOpen size={18} /> : <Mail size={18} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${message.read ? 'text-text-secondary' : 'text-text-primary font-medium'}`}>
                        {message.name}
                      </p>
                      <p className="text-xs text-text-muted truncate">{message.subject}</p>
                      <p className="text-xs text-text-muted mt-1">
                        <Clock size={12} className="inline mr-1" />
                        {formatDate(message.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Message Detail */}
        <div className="lg:col-span-2 bg-surface-secondary border border-white/5">
          {selectedMessage ? (
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="p-6 border-b border-white/10">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="font-playfair text-xl text-text-primary">{selectedMessage.subject}</h2>
                    <div className="flex items-center gap-4 mt-2 text-sm text-text-secondary">
                      <span>{selectedMessage.name}</span>
                      <span className="text-text-muted">•</span>
                      <a href={`mailto:${selectedMessage.email}`} className="text-brand-gold hover:underline">
                        {selectedMessage.email}
                      </a>
                    </div>
                    {selectedMessage.company && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-text-muted">
                        <Building2 size={14} />
                        {selectedMessage.company}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDelete(selectedMessage.id)}
                      className="p-2 text-text-secondary hover:text-red-400 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 size={20} />
                    </button>
                    <button
                      onClick={() => setSelectedMessage(null)}
                      className="p-2 text-text-secondary hover:text-text-primary transition-colors lg:hidden"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-text-muted mt-4">
                  <Clock size={12} className="inline mr-1" />
                  Reçu le {formatDate(selectedMessage.created_at)}
                </p>
              </div>

              {/* Body */}
              <div className="flex-1 p-6 overflow-y-auto">
                <div className="prose prose-invert max-w-none">
                  <p className="text-text-secondary whitespace-pre-wrap leading-relaxed">
                    {selectedMessage.message}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="p-6 border-t border-white/10">
                <a
                  href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <Mail size={18} />
                  Répondre par email
                </a>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-text-muted p-8">
              <div className="text-center">
                <Mail size={48} className="mx-auto mb-4 opacity-30" />
                <p>Sélectionnez un message pour voir son contenu</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
